// A plain QR reader for codes shown on a screen (enrolment and setup): the whole camera
// frame, decoded every 200 ms, no label localiser, no two-frame confirmation. Uses the
// browser's BarcodeDetector where it exists, else ZXing on a downscaled frame.

import { prepareZXingModule, readBarcodes } from "zxing-wasm/reader";
import zxingWasmUrl from "zxing-wasm/reader/zxing_reader.wasm?url";
import { useEffect, useRef, useState } from "react";
import { t } from "../i18n.js";
import { Actions, Btn } from "../ui/components.jsx";

type Detector = { detect: (src: ImageBitmapSource) => Promise<{ rawValue: string }[]> };
declare const BarcodeDetector: (new (opts: { formats: string[] }) => Detector) | undefined;

let zxingReady: Promise<unknown> | null = null;
function zxing(): Promise<unknown> {
  zxingReady ??= prepareZXingModule({ overrides: { locateFile: (p: string) => (p.endsWith(".wasm") ? zxingWasmUrl : p) }, fireImmediately: true });
  return zxingReady;
}

export function QrScan({ onCode, onClose, title }: { onCode: (text: string) => Promise<unknown> | unknown; onClose: () => void; title: string }) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const done = useRef(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    const canvas = document.createElement("canvas");
    const detector = typeof BarcodeDetector !== "undefined" ? new BarcodeDetector({ formats: ["qr_code"] }) : null;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } } });
        const v = video.current;
        if (!v) return;
        v.srcObject = stream;
        await v.play();
        if (!detector) await zxing();
        timer = setInterval(async () => {
          if (done.current || !v.videoWidth) return;
          const scale = Math.min(1, 800 / v.videoWidth);
          canvas.width = Math.round(v.videoWidth * scale);
          canvas.height = Math.round(v.videoHeight * scale);
          const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
          ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
          let text: string | null = null;
          try {
            if (detector) text = (await detector.detect(canvas))[0]?.rawValue ?? null;
            else {
              const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
              text = (await readBarcodes(img, { formats: ["QRCode"], tryHarder: true, maxNumberOfSymbols: 1 }))[0]?.text ?? null;
            }
          } catch {
            text = null;
          }
          if (text && !done.current) {
            done.current = true;
            await onCode(text);
          }
        }, 200);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      if (timer) clearInterval(timer);
      stream?.getTracks().forEach((tr) => tr.stop());
    };
  }, [onCode]);

  return (
    <div className="screen qr-scan">
      <div className="body" style={{ padding: 0, position: "relative", background: "#000" }}>
        <video ref={video} playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        <div className="qr-frame" />
        <p className="hint qr-hint">{error ?? title}</p>
      </div>
      <Actions><Btn kind="quiet" icon="back" onClick={onClose}>{t("common.cancel")}</Btn></Actions>
    </div>
  );
}
